import React, { useEffect, useMemo, useRef, useState } from "react";
import "./HistoryPanel.css";
const ALLIANCE_ICON = require('../assets/alliance.png');
const HORDE_ICON = require('../assets/horde.png');

function HistoryPanel({ history, onClick, onRemove }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [pinnedMap, setPinnedMap] = useState(() => {
    try {
      // Prefer v2 which stores key->timestamp for recency
      const rawV2 = localStorage.getItem("pinnedCharactersV2");
      if (rawV2) {
        const obj = JSON.parse(rawV2);
        return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {};
      }
      // Backward compatibility: v1 stored as an array of keys (no timestamps)
      const raw = localStorage.getItem("pinnedCharacters");
      const arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr)) {
        // Assign ascending timestamps so later items in the array appear more recent
        const base = Date.now() - arr.length;
        const obj = {};
        arr.forEach((k, i) => {
          obj[k] = base + i;
        });
        return obj;
      }
      return {};
    } catch {
      return {};
    }
  });
  const resultsPerPage = 5;
  const touchStartXRef = useRef({});
  const swipedRecentlyRef = useRef({});
  const [swipes, setSwipes] = useState({});
  const [swipeLabels, setSwipeLabels] = useState({});

  const sortedHistory = [...history].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });
  
  const entryKey = (entry) => `${entry.name || ""}|${entry.realm || ""}`;
  const isPinned = (entry) => Object.prototype.hasOwnProperty.call(pinnedMap, entryKey(entry));

  // Build display list: pinned first (by recency desc), then unpinned (by current sort)
  const displayHistory = useMemo(() => {
    const makeKey = (entry) => `${entry.name || ""}|${entry.realm || ""}`;
    const pinnedEntries = [];
    const unpinnedEntries = [];
    for (const entry of sortedHistory) {
      const key = makeKey(entry);
      if (Object.prototype.hasOwnProperty.call(pinnedMap, key)) pinnedEntries.push(entry);
      else unpinnedEntries.push(entry);
    }
    pinnedEntries.sort((a, b) => (pinnedMap[makeKey(b)] || 0) - (pinnedMap[makeKey(a)] || 0));
    return [...pinnedEntries, ...unpinnedEntries];
  }, [sortedHistory, pinnedMap]);

  // Touch handlers for mobile swipe actions
  const handleTouchStart = (e, entry) => {
    const key = entryKey(entry);
    touchStartXRef.current[key] = e.touches && e.touches[0] ? e.touches[0].clientX : 0;
    swipedRecentlyRef.current[key] = false;
  };

  const handleTouchMove = (e, entry) => {
    const key = entryKey(entry);
    const startX = touchStartXRef.current[key] || 0;
    const curX = e.touches && e.touches[0] ? e.touches[0].clientX : startX;
    let dx = curX - startX;
    // Limit slide distance
    if (dx > 120) dx = 120;
    if (dx < -120) dx = -120;
    if (Math.abs(dx) > 8) swipedRecentlyRef.current[key] = true;
    setSwipes((s) => ({ ...s, [key]: dx }));

    // Show action label when user starts to swipe
    const smallMoveThreshold = 8;
    if (Math.abs(dx) > smallMoveThreshold) {
      if (dx > 0) {
        // right swipe -> pin/unpin
        setSwipeLabels((s) => ({ ...s, [key]: isPinned(entry) ? "Unpin" : "Pin" }));
      } else {
        // left swipe -> delete (if pinned, show 'Pinned')
        setSwipeLabels((s) => ({ ...s, [key]: isPinned(entry) ? "Delete" : "Delete" }));
      }
    } else {
      setSwipeLabels((s) => ({ ...s, [key]: null }));
    }
  };

  const handleTouchEnd = (e, entry) => {
    const key = entryKey(entry);
    const dx = swipes[key] || 0;
    const threshold = 50;
    const pinned = isPinned(entry);
    if (dx >= threshold) {
      // swipe right -> toggle pin
      handlePinToggle({ stopPropagation: () => {} }, entry);
    } else if (dx <= -threshold) {
      // swipe left -> delete if not pinned
      if (!pinned) {
        handleRemoveClick({ stopPropagation: () => {} }, entry);
      } else {
        // animate a small left bump to indicate unavailable
        setSwipes((s) => ({ ...s, [key]: -40 }));
        setTimeout(() => setSwipes((s) => ({ ...s, [key]: 0 })), 250);
        touchStartXRef.current[key] = null;
        return;
      }
    }
    // reset translation
    setSwipes((s) => ({ ...s, [key]: 0 }));
  // clear swipe label
  setSwipeLabels((s) => ({ ...s, [key]: null }));
    // mark a short window where clicks should be ignored (prevent accidental click after swipe)
    // Only treat it as a swipe if movement exceeded a small movement threshold; simple taps should not be blocked.
    const smallMoveThreshold = 8;
    if (Math.abs(dx) > smallMoveThreshold) {
      swipedRecentlyRef.current[key] = true;
      setTimeout(() => { swipedRecentlyRef.current[key] = false; }, 300);
    }
    touchStartXRef.current[key] = null;
  };

  const totalPages = Math.ceil(displayHistory.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const currentResults = displayHistory.slice(startIndex, startIndex + resultsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Update resultsPerPage responsively when viewport changes (mobile vs desktop)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updatePageSize = () => {
      setCurrentPage(1); // reset to first page when layout changes
    };
    // Run once to pick correct size
    updatePageSize();
    window.addEventListener('resize', updatePageSize);
    return () => window.removeEventListener('resize', updatePageSize);
  }, []);

  const handleRowClick = (entry) => {
    onClick(entry);
  };

  const handleRemoveClick = (e, entry) => {
    e.stopPropagation(); // Prevent row click when clicking remove button
    if (isPinned(entry)) {
      // Do not allow removal of pinned entries
      return;
    }
    if (onRemove) {
      onRemove(entry);
    }
  };

  const handlePinToggle = (e, entry) => {
    e.stopPropagation();
    const key = entryKey(entry);
    const currentlyPinned = isPinned(entry);
    setPinnedMap((prev) => {
      const next = { ...prev };
      if (currentlyPinned) {
        delete next[key];
      } else {
        next[key] = Date.now();
      }
      return next;
    });
    if (!currentlyPinned) {
      // Bring newest pin to the very first row (page 1)
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem("pinnedCharactersV2", JSON.stringify(pinnedMap));
    } catch {
      // ignore storage errors (e.g., privacy mode)
    }
  }, [pinnedMap]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return { key, direction: prevConfig.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  if (!history.length) return null;

  return (
    <div className="history-panel">
      <h3>Search history</h3>
      
      {/* Desktop table view */}
      <table className="history-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("name")}>Name</th>
            <th onClick={() => handleSort("faction")}>Faction</th>
            <th onClick={() => handleSort("realm")}>Realm</th>
            <th onClick={() => handleSort("level")}>Level</th>
            <th onClick={() => handleSort("race")}>Race</th>
            <th onClick={() => handleSort("class")}>Class</th>
            <th onClick={() => handleSort("gearScore")}>GS</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentResults.map((entry, idx) => (
            <tr
              key={entry.name + entry.realm}
              className={entry.class ? entry.class.replace(" ", "") : "Unknown"} // Remove spaces for class names
              onClick={() => handleRowClick(entry)}
              style={{ cursor: "pointer" }}
            >
              <td className="history-name-cell">
                {(() => {
                  const f = (entry.faction || '').toString().toLowerCase();
                  if(f.includes('horde')) return <img src={HORDE_ICON} alt="Horde" className="history-faction-icon" />;
                  if(f.includes('alliance')) return <img src={ALLIANCE_ICON} alt="Alliance" className="history-faction-icon" />;
                  return null;
                })()}
                <span>{entry.name || 'Unknown'}</span>
              </td>
              <td>{entry.faction || 'Unknown'}</td>
              <td>{entry.realm || 'Unknown'}</td>
              <td>{entry.level || 'Unknown'}</td>
              <td>{entry.race || 'Unknown'}</td>
              <td>{entry.class || 'Unknown'}</td>
              <td>{entry.gearScore ?? 0}</td>
              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className={`pin-btn ${isPinned(entry) ? 'pinned' : ''}`}
                    onClick={(e) => handlePinToggle(e, entry)}
                    title={isPinned(entry) ? "Unpin character" : "Pin character"}
                    aria-label={isPinned(entry) ? "Unpin character" : "Pin character"}
                  >
                    {isPinned(entry) ? '📍' : '📌'}
                  </button>
                  <button 
                    className="remove-btn"
                    onClick={(e) => handleRemoveClick(e, entry)}
                    title={isPinned(entry) ? "Pinned characters cannot be removed" : "Remove from history"}
                    aria-label={isPinned(entry) ? "Pinned characters cannot be removed" : "Remove from history"}
                    disabled={isPinned(entry)}
                  >
                    ✕
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile card view */}
      <div className="mobile-history-cards">
        {currentResults.map((entry, idx) => {
          const key = entryKey(entry);
          const dx = swipes[key] || 0;
          // fade the card as it slides: min opacity 0.35 when fully swiped
          const opacity = Math.max(0.35, 1 - Math.abs(dx) / 140);
          return (
          <div
            key={entry.name + entry.realm}
            className={`history-card ${entry.class ? entry.class.replace(" ", "") : "Unknown"}${isPinned(entry) ? ' pinned' : ''}`}
            onClick={() => {
              // ignore click if user just swiped
              if (swipedRecentlyRef.current[entryKey(entry)]) return;
              handleRowClick(entry);
            }}
            onTouchStart={(e) => handleTouchStart(e, entry)}
            onTouchMove={(e) => handleTouchMove(e, entry)}
            onTouchEnd={(e) => handleTouchEnd(e, entry)}
            style={{ transform: `translateX(${dx}px)`, opacity }}
          >
            {/* Overlay actions on the right without taking horizontal space */}
            <div className="history-card-actions">
              {isPinned(entry) ? (
                // Non-interactive pinned indicator for mobile (same visual format)
                <div className="pin-indicator-mobile" title="Pinned" aria-hidden>
                  📍
                </div>
              ) : null}
            </div>
              {/* Swipe action label (appears while user is swiping) */}
              {swipeLabels[entryKey(entry)] ? (
                <div className={`swipe-action ${ (swipes[entryKey(entry)]||0) > 0 ? 'left' : 'right' }`}>
                  {swipeLabels[entryKey(entry)]}
                </div>
              ) : null}
            <div className="history-card-header">
              <div className="history-card-name" style={{display:'flex',alignItems:'center',gap:8}}>
                {(() => {
                  const f = (entry.faction || '').toString().toLowerCase();
                  if(f.includes('horde')) return <img src={HORDE_ICON} alt="Horde" className="history-faction-icon" />;
                  if(f.includes('alliance')) return <img src={ALLIANCE_ICON} alt="Alliance" className="history-faction-icon" />;
                  return null;
                })()}
                <span className="history-card-name-value">{entry.name || 'Unknown'}</span>
              </div>
            </div>
            <div className="history-card-details">
              <div className="history-card-detail">
                <span className="history-card-detail-label">Realm:</span>
                <span className="history-card-realm-value">{entry.realm || 'Unknown'}</span>
              </div>
              <div className="history-card-detail">
                <span className="history-card-detail-label">Level:</span>
                <span className="history-card-level-value">{entry.level || 'Unknown'}</span>
              </div>
              <div className="history-card-detail">
                <span className="history-card-detail-label">Race:</span>
                <span className="history-card-race-value">{entry.race || 'Unknown'}</span>
              </div>
              <div className="history-card-detail">
                <span className="history-card-detail-label">Class:</span>
                <span className="history-card-class-value">{entry.class || 'Unknown'}</span>
              </div>
              <div className="history-card-detail">
                <span className="history-card-detail-label">GS:</span>
                <span className="history-card-gearscore-value">{entry.gearScore ?? 0}</span>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
            &#8592; {/* Left arrow */}
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
            &#8594; {/* Right arrow */}
          </button>
        </div>
      )}
    </div>
  );
}

export default HistoryPanel;
